import { postIpc } from "../../lib/ipc";
import { useAuth } from "../../context/AuthContext";

type DockEntry = { id: string; label: string; icon: string };

const DOCK_ITEMS: readonly DockEntry[] = [
  { id: "home", label: "Home", icon: "M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z M9 22V12h6v10" },
  { id: "chat", label: "Chat", icon: "M5 3l14 9-14 9V3z" },
  { id: "browser", label: "Browser", icon: "M12 2a10 10 0 100 20 10 10 0 000-20z M2 12h20 M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10A15.3 15.3 0 0112 2z" },
  { id: "files", label: "Files", icon: "M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z M14 2v6h6" },
  { id: "canvas", label: "Canvas", icon: "M3 3h18v18H3z M3 9h18 M9 21V9" },
  { id: "projects", label: "Projects", icon: "M2 3h20v14a2 2 0 01-2 2H4a2 2 0 01-2-2V3z M8 21h8 M12 17v4" },
];

interface SidebarNavProps {
  activeRoute: string;
  onNavigate: (route: string) => void;
  appName?: string;
  onOpenApps: () => void;
  appsOpen?: boolean;
}

export function SidebarNav({ activeRoute, onNavigate, appName = "C", onOpenApps, appsOpen }: SidebarNavProps) {
  const { user, logout } = useAuth();

  return (
    <nav className="shell-sidebar">
      {/* Logo */}
      <div className="shell-sidebar__logo">{appName[0]}</div>

      {/* Core dock items */}
      <div className="shell-sidebar__dock">
        {DOCK_ITEMS.map((item) => (
          <button
            key={item.id}
            className={`shell-sidebar__item ${activeRoute === item.id ? "shell-sidebar__item--active" : ""}`}
            title={item.label}
            onClick={() => {
              onNavigate(item.id);
              postIpc({ kind: `section-${item.id}` });
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d={item.icon} />
            </svg>
            <span className="shell-sidebar__tip">{item.label}</span>
          </button>
        ))}
      </div>

      {/* Separator */}
      <div className="shell-sidebar__separator" />

      {/* Apps launcher button */}
      <button
        className={`shell-sidebar__item shell-sidebar__apps-btn ${appsOpen ? "shell-sidebar__item--active" : ""}`}
        title="Apps"
        onClick={onOpenApps}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="7" rx="1.5" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" />
          <rect x="14" y="14" width="7" height="7" rx="1.5" />
        </svg>
        <span className="shell-sidebar__tip">Apps</span>
      </button>

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* User avatar at bottom */}
      <div className="shell-sidebar__user">
        {user ? (
          <button
            className="shell-sidebar__avatar"
            title={`${user.login} (click to logout)`}
            onClick={logout}
          >
            <span className="shell-sidebar__avatar-letter">
              {user.login[0]?.toUpperCase() || "U"}
            </span>
            <span className="shell-sidebar__avatar-ring" />
          </button>
        ) : (
          <button
            className="shell-sidebar__avatar shell-sidebar__avatar--anon"
            title="Not signed in"
            onClick={() => onNavigate("home")}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </button>
        )}
      </div>
    </nav>
  );
}
